/**
 * @module features/users/components/CreateUserDrawer
 * @description Drawer for onboarding new super admins or clients with instant validation,
 * passkey device limit policy details, and enterprise security parameters.
 */

import * as React from "react";
import { useForm } from "react-hook-form";
import {
  Loader2,
  AlertCircle,
  Shield,
  User as UserIcon,
  Fingerprint,
} from "lucide-react";
import { Drawer } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCreateUser } from "../api/users-api";
import { AppError } from "@/lib/api-client";
import { CreateUserInput } from "@/types/users";

interface CreateUserDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  name: string;
  email_id: string;
  role: "super_admin" | "client";
  country_code: string;
  contact_number: string;
  password?: string;
  max_passkeys: number;
}

export function CreateUserDrawer({ open, onClose }: CreateUserDrawerProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: "",
      email_id: "",
      role: "client",
      country_code: "+1",
      contact_number: "",
      password: "",
      max_passkeys: 5,
    },
  });

  const selectedRole = watch("role");
  const maxPasskeys = watch("max_passkeys");
  const createUserMutation = useCreateUser();
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const onSubmit = async (values: FormValues) => {
    setErrorMessage(null);

    const payload: CreateUserInput = {
      name: values.name.trim(),
      email_id: values.email_id.trim().toLowerCase(),
      role: values.role,
      country_code: values.country_code.trim(),
      contact_number: values.contact_number.trim(),
      password: values.password?.trim() || undefined,
      max_passkeys: Number(values.max_passkeys) || 5,
    };

    try {
      await createUserMutation.mutateAsync(payload);
      reset();
      onClose();
    } catch (err: any) {
      if (err instanceof AppError) {
        setErrorMessage(err.errorDetails);
      } else {
        setErrorMessage("Failed to create user. Please check the inputs.");
      }
    }
  };

  const handleClose = () => {
    reset();
    setErrorMessage(null);
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Create User"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        {errorMessage && (
          <div className="flex items-center gap-2 p-3 text-xs rounded-lg bg-danger-soft text-danger border border-danger/20">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Role Selection */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-foreground block">
            Account Role
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setValue("role", "client")}
              className={`flex items-center gap-2.5 p-3 rounded-lg border text-xs font-medium transition-all ${
                selectedRole === "client"
                  ? "border-purple-500 bg-purple-500/10 text-purple-600 dark:text-purple-400 font-semibold ring-1 ring-purple-500"
                  : "border-border bg-card text-muted-foreground hover:bg-muted"
              }`}
            >
              <UserIcon className="h-4 w-4 shrink-0" />
              <span>Client User</span>
            </button>
            <button
              type="button"
              onClick={() => setValue("role", "super_admin")}
              className={`flex items-center gap-2.5 p-3 rounded-lg border text-xs font-medium transition-all ${
                selectedRole === "super_admin"
                  ? "border-zinc-950 dark:border-zinc-100 bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 font-semibold ring-1 ring-zinc-950 dark:ring-zinc-100"
                  : "border-border bg-card text-muted-foreground hover:bg-muted"
              }`}
            >
              <Shield className="h-4 w-4 shrink-0" />
              <span>Super Admin</span>
            </button>
          </div>
        </div>

        {/* Full Name */}
        <div className="space-y-1">
          <label htmlFor="name" className="text-xs font-medium text-foreground block">
            Full Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="name"
            autoComplete="name"
            placeholder="e.g. Jane Doe"
            {...register("name", { required: "Name is required." })}
            className="h-10 text-sm bg-card"
          />
          {errors.name && (
            <p className="text-[11px] text-destructive">{errors.name.message}</p>
          )}
        </div>

        {/* Email Address */}
        <div className="space-y-1">
          <label htmlFor="email" className="text-xs font-medium text-foreground block">
            Email Address <span className="text-destructive">*</span>
          </label>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="name@company.com"
            {...register("email_id", {
              required: "Email is required.",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Please enter a valid email address.",
              },
            })}
            className="h-10 text-sm bg-card"
          />
          {errors.email_id && (
            <p className="text-[11px] text-destructive">
              {errors.email_id.message}
            </p>
          )}
        </div>

        {/* Phone info: Country Code + Phone */}
        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1 col-span-1">
            <label
              htmlFor="country_code"
              className="text-xs font-medium text-foreground block"
            >
              Code
            </label>
            <Input
              id="country_code"
              placeholder="+1"
              {...register("country_code")}
              className="h-10 text-sm font-mono bg-card"
            />
          </div>
          <div className="space-y-1 col-span-2">
            <label
              htmlFor="contact_number"
              className="text-xs font-medium text-foreground block"
            >
              Contact Number
            </label>
            <Input
              id="contact_number"
              type="tel"
              inputMode="tel"
              placeholder="555-0199"
              {...register("contact_number")}
              className="h-10 text-sm font-mono bg-card"
            />
          </div>
        </div>

        {/* Optional Password */}
        <div className="space-y-1">
          <label
            htmlFor="password"
            className="text-xs font-medium text-foreground block"
          >
            Password (Optional)
          </label>
          <Input
            id="password"
            type="password"
            placeholder="Leave blank for SSO / passkey-only"
            {...register("password")}
            className="h-10 text-sm font-mono bg-card"
          />
        </div>

        {/* Simple Configurable Passkey Limit */}
        <div className="rounded-xl border border-border/80 dark:border-zinc-800 bg-muted/20 dark:bg-zinc-900/40 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <label htmlFor="max_passkeys" className="text-xs font-medium text-foreground flex items-center gap-1.5">
              <Fingerprint className="h-4 w-4 text-foreground shrink-0" />
              <span>Passkey Device Limit</span>
            </label>
            <span className="text-[11px] font-mono text-muted-foreground">
              {maxPasskeys || 5} Max
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Input
              id="max_passkeys"
              type="number"
              min={1}
              max={20}
              {...register("max_passkeys", { valueAsNumber: true, min: 1, max: 20 })}
              className="h-8 w-16 text-center font-mono text-xs bg-card"
            />
            <div className="flex items-center gap-1.5 flex-wrap">
              {[1, 3, 5, 10].map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() => setValue("max_passkeys", count)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-all ${
                    Number(maxPasskeys) === count
                      ? "bg-zinc-950 text-white dark:bg-zinc-100 dark:text-zinc-950 border-zinc-950 dark:border-zinc-100 font-semibold shadow-xs"
                      : "bg-card border-border hover:bg-muted text-muted-foreground"
                  }`}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-3 flex items-center justify-end gap-2 border-t border-border mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            className="h-9 px-4 text-xs"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createUserMutation.isPending}
            className="h-9 px-4 text-xs min-w-[110px] shadow-sm bg-zinc-950 text-white hover:bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 dark:hover:bg-white"
          >
            {createUserMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create User"
            )}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
